import { changeLine, addLineItems, refreshCart, fetchCart } from './themeCart.js'

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(cents) / 100)
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || ''}`.trim()
  }
}

// The cart drawer lives inside the `header` section. Re-rendering that section
// server-side (Section Rendering API) is the only way to rebuild the rich line
// items with full Liquid data (metafields, variant list, selling-plan groups),
// which `/cart.js` JSON does not expose.
const CART_SECTION = 'header'

class CartPanel extends HTMLElement {
  #abort
  #cart = null
  #busy = false
  #prevCount = -1
  #hydrated = false

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    document.addEventListener(
      'theme:cart:updated',
      (e) => {
        const cart = e.detail.cart
        const prevCount = this.#prevCount
        this.#cart = cart
        this.#prevCount = cart.item_count
        this.#renderSummary()

        // The server-rendered markup is already correct on first paint, so skip
        // the extra section fetch on initial hydration. Any later update (PDP
        // add, qty change, etc.) re-renders the line items from the section.
        if (this.#hydrated) {
          this.#renderItemsFromSection()
        } else {
          this.#hydrated = true
        }

        // Auto-open the drawer when items get added (the typical PDP
        // add-to-cart flow), but skip it when the user is already viewing
        // the full cart page.
        const onCartPage =
          document.querySelector('cart-page') !== null ||
          globalThis.location.pathname === '/cart' ||
          globalThis.location.pathname.endsWith('/cart')
        if (prevCount >= 0 && cart.item_count > prevCount && !onCartPage) {
          this.open()
        }
      },
      { signal },
    )

    this.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('[data-cart-close]')) {
          this.close()
        }
      },
      { signal },
    )

    document.addEventListener(
      'click',
      (e) => {
        const toggle = e.target.closest('[data-cart-toggle]')
        if (!toggle) return
        e.preventDefault()
        if (this.hasAttribute('open')) {
          this.close()
        } else {
          this.open()
        }
      },
      { signal },
    )

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) {
          this.close()
        }
      },
      { signal },
    )

    // Quantity stepper + remove.
    this.addEventListener(
      'click',
      (e) => {
        const minus = e.target.closest('[data-cart-qty-minus]')
        const plus = e.target.closest('[data-cart-qty-plus]')
        const remove = e.target.closest('[data-cart-remove]')
        if (!minus && !plus && !remove) return

        const item = e.target.closest('[data-cart-item]')
        if (!item) return
        const line = Number.parseInt(item.dataset.line, 10)
        if (!line) return

        const qtyEl = item.querySelector('[data-cart-qty-value]')
        const currentQty = Number.parseInt(qtyEl?.textContent || '1', 10)

        let newQty
        if (remove) {
          newQty = 0
        } else if (minus) {
          newQty = Math.max(0, currentQty - 1)
        } else {
          newQty = currentQty + 1
        }

        this.#mutate(item, () => changeLine(line, newQty))
      },
      { signal },
    )

    // Variant (pack) selector.
    this.addEventListener(
      'change',
      (e) => {
        const variantSelect = e.target.closest('[data-cart-item-variant]')
        if (!variantSelect) return

        const item = e.target.closest('[data-cart-item]')
        if (!item) return
        const line = Number.parseInt(item.dataset.line, 10)
        if (!line) return

        const newVariantId = Number.parseInt(variantSelect.value, 10)
        const currentVariantId = Number.parseInt(item.dataset.variantId || '0', 10)
        if (!newVariantId || newVariantId === currentVariantId) return
        this.#changeVariant(item, line, newVariantId)
      },
      { signal },
    )
  }

  disconnectedCallback() {
    this.#abort?.abort()
  }

  open() {
    this.toggleAttribute('open', true)
    this.removeAttribute('inert')
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('cart-open')
  }

  close() {
    this.removeAttribute('open')
    this.setAttribute('inert', '')
    this.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('cart-open')
  }

  // Run a cart mutation with an optimistic busy state, then refresh. The
  // `theme:cart:updated` listener re-renders the line items from the section.
  async #mutate(item, action) {
    if (this.#busy) return
    this.#busy = true
    if (item) {
      item.style.opacity = '0.5'
      item.style.pointerEvents = 'none'
    }
    try {
      await action()
      await refreshCart()
    } catch (err) {
      console.error(err)
      if (item) {
        item.style.opacity = ''
        item.style.pointerEvents = ''
      }
    } finally {
      this.#busy = false
    }
  }

  // Changing a line's variant isn't supported by /cart/change.js, so remove the
  // current line and re-add the chosen variant, preserving quantity, plan, and
  // any line item properties (e.g. build-a-box flavor selections).
  async #changeVariant(item, line, newVariantId) {
    if (this.#busy) return
    const qty = Number.parseInt(item.querySelector('[data-cart-qty-value]')?.textContent || '1', 10)
    const planId = Number.parseInt(item.dataset.sellingPlan || '0', 10)
    await this.#mutate(item, async () => {
      // Capture the line's properties before removing it — change.js can't move
      // them to the new variant, so they'd be lost on the re-add otherwise.
      let properties = null
      try {
        const current = await fetchCart()
        properties = current?.items?.[line - 1]?.properties || null
      } catch {
        /* fall back to no properties */
      }
      await changeLine(line, 0)
      const lineItem = { id: newVariantId, quantity: qty || 1 }
      if (planId) lineItem.selling_plan = planId
      if (properties && Object.keys(properties).length) lineItem.properties = properties
      await addLineItems([lineItem])
    })
  }

  #renderSummary() {
    if (!this.#cart) return
    const cart = this.#cart
    const threshold = Number.parseInt(this.dataset.freeShippingThreshold || '5000', 10)

    this.querySelectorAll('[data-cart-panel-count]').forEach((el) => {
      el.textContent = `(${cart.item_count})`
    })

    const subtotalEl = this.querySelector('[data-cart-subtotal]')
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price, cart.currency)
    }

    const subtotalCountEl = this.querySelector('[data-cart-subtotal-count]')
    if (subtotalCountEl) {
      subtotalCountEl.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`
    }

    const shippingMessage = this.querySelector('[data-cart-shipping-message]')
    if (shippingMessage) {
      const remaining = threshold - cart.total_price
      if (remaining > 0) {
        shippingMessage.textContent = `Spend ${formatMoney(remaining, cart.currency)} more to get Free Shipping`
      } else {
        shippingMessage.textContent = 'You qualify for Free Shipping!'
      }
    }

    const shippingFilled = this.querySelector('[data-cart-shipping-filled]')
    if (shippingFilled) {
      const progress = Math.min(100, (cart.total_price / threshold) * 100)
      shippingFilled.style.width = `${progress}%`
    }
  }

  // Pull a fresh render of the cart line items from the `header` section and
  // swap them into the live drawer, keeping all the rich Liquid markup.
  async #renderItemsFromSection() {
    const live = this.querySelector('[data-cart-items]')
    if (!live) return

    try {
      const root = globalThis.Shopify?.routes?.root || '/'
      const url = `${root}?sections=${CART_SECTION}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      const data = await response.json()
      const html = data?.[CART_SECTION]
      if (!html) return

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const fresh = doc.querySelector('[data-cart-items]')
      if (fresh) {
        live.innerHTML = fresh.innerHTML
      }
    } catch (err) {
      console.error(err)
    }
  }
}

if (!customElements.get('cart-panel')) {
  customElements.define('cart-panel', CartPanel)
}
